import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { BellRing, KeyRound, LockKeyhole, RotateCcw, Upload } from "lucide-react";
import { V1_SYMBOLS, type Signal, type WatchSymbol } from "@tradesignal/core";
import { Badge } from "./components/Badge";
import { ChartCard } from "./components/ChartCard";
import { Shell } from "./components/Shell";
import { rankedBySymbol, signals, symbolCandles, trades as mockTrades, watchlist as defaultWatchlist } from "./data/mock";
import { requestNotifications } from "./lib/pwa";
import { importManualCsvData, listWatchlist, saveWatchSymbol } from "./lib/services";
import { allowedUserEmail, supabase } from "./lib/supabase";

export type View = "dashboard" | "watchlist" | "signals" | "signalDetail" | "trades" | "tradeDetail" | "lab" | "settings";

export default function App() {
  const [signedIn, setSignedIn] = useState(!supabase);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [view, setView] = useState<View>("dashboard");
  const [watchlist, setWatchlist] = useState<WatchSymbol[]>(defaultWatchlist);
  const [selectedSignal, setSelectedSignal] = useState<Signal | null>(signals[0] ?? null);
  const [selectedTrade, setSelectedTrade] = useState(mockTrades[0]);
  const [notice, setNotice] = useState("Data is demo/historical until Supabase and Worker secrets are configured.");
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    listWatchlist()
      .then(setWatchlist)
      .catch((error: unknown) => setNotice(error instanceof Error ? error.message : "Could not load watchlist."));
  }, []);

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => {
      setSignedIn(Boolean(data.session));
      setUserId(data.session?.user.id ?? null);
    });
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSignedIn(Boolean(session));
      setUserId(session?.user.id ?? null);
      if (session) setNotice("Supabase session active. Live persistence is enabled.");
    });
    return () => subscription.unsubscribe();
  }, []);

  function validateOwnerEmail() {
    if (allowedUserEmail && email.toLowerCase() !== allowedUserEmail.toLowerCase()) {
      setNotice("This private app only allows the configured owner email.");
      return false;
    }
    return true;
  }

  async function loginWithPassword() {
    if (!validateOwnerEmail()) return;
    if (!supabase) {
      setSignedIn(true);
      setNotice("Local demo mode active. Configure Supabase for private internet access.");
      return;
    }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setNotice(error ? "Password login failed. Use the magic-link fallback once, then set a password in Settings." : "Signed in with password.");
  }

  async function sendMagicLink() {
    if (!validateOwnerEmail()) return;
    if (!supabase) {
      setSignedIn(true);
      setNotice("Local demo mode active. Configure Supabase for private internet access.");
      return;
    }
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin
      }
    });
    setNotice(error ? error.message : "Magic link sent. Use it once, then set a password in Settings.");
  }

  function logout() {
    supabase?.auth.signOut();
    setSignedIn(!supabase);
    setView("dashboard");
  }

  if (!signedIn) {
    return (
      <Login
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        loginWithPassword={loginWithPassword}
        sendMagicLink={sendMagicLink}
        notice={notice}
      />
    );
  }

  return (
    <Shell view={view} setView={setView} onLogout={logout}>
      {view === "dashboard" ? <Dashboard notice={notice} setView={setView} onSelectSignal={(signal) => { setSelectedSignal(signal); setView("signalDetail"); }} /> : null}
      {view === "watchlist" ? <Watchlist items={watchlist} setItems={setWatchlist} setNotice={setNotice} /> : null}
      {view === "signals" ? <Signals onSelect={(signal) => { setSelectedSignal(signal); setView("signalDetail"); }} /> : null}
      {view === "signalDetail" && selectedSignal ? <SignalDetail signal={selectedSignal} /> : null}
      {view === "trades" ? <Trades onSelect={(trade) => { setSelectedTrade(trade); setView("tradeDetail"); }} /> : null}
      {view === "tradeDetail" ? <TradeDetail trade={selectedTrade} /> : null}
      {view === "lab" ? <StrategyLab /> : null}
      {view === "settings" ? <Settings notice={notice} setNotice={setNotice} userId={userId} /> : null}
    </Shell>
  );
}

function Login({
  email,
  setEmail,
  password,
  setPassword,
  loginWithPassword,
  sendMagicLink,
  notice
}: {
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  loginWithPassword: () => void;
  sendMagicLink: () => void;
  notice: string;
}) {
  return (
    <div className="safe-shell mx-auto flex min-h-dvh w-full max-w-[430px] flex-col justify-between px-5">
      <div className="pt-12">
        <div className="mb-8 inline-flex rounded-full border border-violet/30 bg-violet/15 p-4 text-violet-100 shadow-glow">
          <LockKeyhole />
        </div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-rose">Private PWA</p>
        <h1 className="mt-3 text-4xl font-extrabold tracking-normal">TradeSignal Command Center</h1>
        <p className="mt-4 text-sm leading-6 text-slate-300">
          Trading signal analysis, notifications, strategy backtesting, and manual trade tracking only. No broker connection and no real trade execution.
        </p>
      </div>
      <form
        className="glass-card rounded-[2rem] p-5"
        onSubmit={(event) => {
          event.preventDefault();
          void loginWithPassword();
        }}
      >
        <label className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Owner email</label>
        <input
          className="mt-3 w-full rounded-3xl border border-line bg-ink px-4 py-4 text-white outline-none ring-violet/30 focus:ring-4"
          placeholder="you@example.com"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <label className="mt-4 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Password</label>
        <input
          className="mt-3 w-full rounded-3xl border border-line bg-ink px-4 py-4 text-white outline-none ring-violet/30 focus:ring-4"
          placeholder="Private password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <button type="submit" className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet to-rose px-5 py-4 font-extrabold text-white shadow-glow">
          <KeyRound size={18} /> Sign in with password
        </button>
        <button type="button" onClick={sendMagicLink} className="mt-3 w-full rounded-full border border-line bg-panel px-5 py-4 text-sm font-extrabold text-white">
          Send one-time email link
        </button>
        <p className="mt-4 text-xs leading-5 text-slate-400">{notice}</p>
      </form>
    </div>
  );
}

function Dashboard({ notice, setView, onSelectSignal }: { notice: string; setView: (view: View) => void; onSelectSignal: (signal: Signal) => void }) {
  const lastScan = new Date(Date.now() - 17 * 60 * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return (
    <>
      <section className="accent-card rounded-[2rem] p-5 shadow-glow">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">After-close scan</p>
            <h2 className="mt-2 text-3xl font-extrabold tracking-normal">Last scan {lastScan}</h2>
            <p className="mt-3 text-sm leading-6 text-slate-300">{notice}</p>
          </div>
          <Badge tone="warn">Delayed</Badge>
        </div>
      </section>

      <div className="grid grid-cols-2 gap-3">
        <Metric label="Enabled symbols" value={String(defaultWatchlist.filter((item) => item.enabled).length)} tone="violet" />
        <Metric label="Open trades" value={String(mockTrades.length)} tone="good" />
        <Metric label="High-score signals" value={String(signals.length)} tone="warn" />
        <Metric label="Risk cap" value="3 max" tone="neutral" />
      </div>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold">Watchlist overview</h2>
          <button onClick={() => setView("watchlist")} className="text-xs font-extrabold text-rose">Manage</button>
        </div>
        {defaultWatchlist.slice(0, 5).map((item) => {
          const signal = signals.find((candidate) => candidate.symbol === item.symbol);
          const top = rankedBySymbol[item.symbol]?.[0];
          return (
            <button key={item.id} onClick={() => signal && onSelectSignal(signal)} className="glass-card w-full rounded-[1.75rem] p-4 text-left">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-extrabold">{item.symbol}</h3>
                  <p className="mt-1 text-xs text-slate-400">#{top?.rank ?? 1} {top?.strategyName ?? "No ranking yet"}</p>
                </div>
                <Badge tone={signal ? "good" : "neutral"}>{signal ? `${signal.confidence}/100` : "No signal"}</Badge>
              </div>
              <p className="mt-3 text-sm text-slate-300">{signal?.explanation ?? "Waiting for a setup that clears the 80/100 threshold."}</p>
            </button>
          );
        })}
      </section>
    </>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone: "violet" | "good" | "warn" | "neutral" }) {
  return (
    <div className="glass-card rounded-[1.75rem] p-4">
      <Badge tone={tone}>{label}</Badge>
      <p className="mt-4 text-3xl font-extrabold">{value}</p>
    </div>
  );
}

function Watchlist({ items, setItems, setNotice }: { items: WatchSymbol[]; setItems: (items: WatchSymbol[]) => void; setNotice: (value: string) => void }) {
  async function updateItem(nextItem: WatchSymbol) {
    const nextItems = items.map((item) => (item.id === nextItem.id ? nextItem : item));
    setItems(nextItems);
    try {
      await saveWatchSymbol(nextItem);
      setNotice(`${nextItem.symbol} watchlist settings saved.`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not save watchlist settings.");
    }
  }

  function restoreV1Symbols() {
    setItems(defaultWatchlist);
    defaultWatchlist.forEach((item) => {
      void saveWatchSymbol(item).catch((error: unknown) => setNotice(error instanceof Error ? error.message : "Could not save watchlist settings."));
    });
    setNotice("Restored the Addendum 1 v1 watchlist.");
  }
  return (
    <section className="space-y-3">
      <button
        onClick={restoreV1Symbols}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-4 font-extrabold text-ink"
      >
        <RotateCcw size={18} /> Restore v1 watchlist
      </button>
      {items.map((item) => (
        <div key={item.id} className="glass-card rounded-[1.75rem] p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-extrabold">{item.symbol}</h2>
              <p className="mt-1 text-xs text-slate-500">Addendum 1 v1 symbol</p>
            </div>
            <button onClick={() => updateItem({ ...item, enabled: !item.enabled })} className={`rounded-full px-4 py-2 text-xs font-extrabold ${item.enabled ? "bg-mint text-ink" : "bg-slate-800 text-slate-300"}`}>
              {item.enabled ? "Enabled" : "Disabled"}
            </button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <select className="rounded-2xl border border-line bg-ink px-3 py-3 text-sm" value={item.assetType} onChange={(event) => updateItem({ ...item, assetType: event.target.value as WatchSymbol["assetType"] })}>
              <option>stock</option><option>ETF</option>
            </select>
            <select className="rounded-2xl border border-line bg-ink px-3 py-3 text-sm" value={item.timeframe} onChange={(event) => updateItem({ ...item, timeframe: event.target.value as WatchSymbol["timeframe"] })}>
              <option>daily</option>
            </select>
          </div>
        </div>
      ))}
    </section>
  );
}

function Signals({ onSelect }: { onSelect: (signal: Signal) => void }) {
  return (
    <section className="space-y-3">
      {signals.map((signal) => (
        <button key={signal.id} onClick={() => onSelect(signal)} className="glass-card w-full rounded-[1.75rem] p-4 text-left">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-extrabold">{signal.symbol}</h2>
              <p className="text-sm text-slate-400">{signal.strategyName}</p>
            </div>
            <Badge tone={signal.direction === "LONG" ? "good" : "danger"}>{signal.direction}</Badge>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
            <Level label="Entry" value={signal.entry} />
            <Level label="Stop" value={signal.stopLoss} />
            <Level label="T2" value={signal.target2} />
          </div>
        </button>
      ))}
    </section>
  );
}

function SignalDetail({ signal }: { signal: Signal }) {
  return (
    <section className="space-y-4">
      <div className="accent-card rounded-[2rem] p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-rose">Signal detail</p>
            <h2 className="mt-1 text-4xl font-extrabold">{signal.symbol}</h2>
          </div>
          <Badge tone="good">{signal.confidence}/100</Badge>
        </div>
        <p className="mt-4 text-sm leading-6 text-slate-300">{signal.explanation}</p>
      </div>
      <ChartCard candles={symbolCandles[signal.symbol]} signal={signal} />
      <div className="grid grid-cols-2 gap-3">
        <Level label="Entry" value={signal.entry} />
        <Level label="Stop loss" value={signal.stopLoss} />
        <Level label="Target 1" value={signal.target1} />
        <Level label="Target 2" value={signal.target2} />
      </div>
      <section className="glass-card rounded-[2rem] p-5">
        <p className="text-sm leading-6 text-slate-300">Invalidated if: {signal.invalidatedIf}</p>
        <p className="mt-2 text-xs text-slate-500">Data: {signal.freshness}, timestamp {new Date(signal.dataTimestamp).toLocaleString()}</p>
      </section>
    </section>
  );
}

function Trades({ onSelect }: { onSelect: (trade: typeof mockTrades[number]) => void }) {
  return (
    <section className="space-y-3">
      {mockTrades.map((trade) => (
        <button key={trade.id} onClick={() => onSelect(trade)} className="glass-card w-full rounded-[1.75rem] p-4 text-left">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-extrabold">{trade.symbol}</h2>
              <p className="text-sm text-slate-400">{trade.status} at {trade.entry}</p>
            </div>
            <Badge tone={trade.recommendation === "HOLD" ? "good" : "warn"}>{trade.recommendation}</Badge>
          </div>
        </button>
      ))}
    </section>
  );
}

function TradeDetail({ trade }: { trade: typeof mockTrades[number] }) {
  const pnl = trade.direction === "LONG" ? trade.latestPrice - trade.entry : trade.entry - trade.latestPrice;
  return (
    <section className="space-y-4">
      <div className="accent-card rounded-[2rem] p-5">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">Manual trade tracking</p>
        <h2 className="mt-2 text-4xl font-extrabold">{trade.symbol}</h2>
        <Badge tone={pnl >= 0 ? "good" : "danger"}>{pnl >= 0 ? "+" : ""}{pnl.toFixed(2)} est.</Badge>
      </div>
      <ChartCard candles={symbolCandles[trade.symbol]} />
      <div className="grid grid-cols-2 gap-3">
        <Level label="Entry" value={trade.entry} />
        <Level label="Latest" value={trade.latestPrice} />
        <Level label="Stop" value={trade.stopLoss} />
        <Level label="Target 1" value={trade.target1} />
      </div>
      <section className="glass-card rounded-[2rem] p-5">
        <h3 className="font-extrabold">Current recommendation</h3>
        <p className="mt-2 text-sm leading-6 text-slate-300">{trade.recommendation}. Stops can only stay fixed, tighten, or move to breakeven after entry.</p>
      </section>
    </section>
  );
}

function StrategyLab() {
  const top = Object.entries(rankedBySymbol).slice(0, 5);
  return (
    <section className="space-y-3">
      {top.map(([symbol, rankings]) => (
        <div key={symbol} className="glass-card rounded-[1.75rem] p-4">
          <h2 className="text-xl font-extrabold">{symbol}</h2>
          <div className="mt-4 space-y-3">
            {rankings.slice(0, 3).map((ranking) => (
              <div key={ranking.strategyId} className="flex items-center justify-between gap-3">
                <p className="text-sm font-bold">#{ranking.rank} {ranking.strategyName}</p>
                <Badge tone={ranking.score >= 70 ? "good" : "warn"}>{ranking.score}</Badge>
              </div>
            ))}
          </div>
        </div>
      ))}
      <section className="glass-card rounded-[2rem] p-5">
        <h3 className="font-extrabold">Backtest controls</h3>
        <p className="mt-2 text-sm leading-6 text-slate-400">
          Rankings use profit factor, expectancy, drawdown, recent performance, out-of-sample expectancy, and trade count. Low sample and overfit warnings reduce score.
        </p>
      </section>
    </section>
  );
}

function Settings({ notice, setNotice, userId }: { notice: string; setNotice: (value: string) => void; userId: string | null }) {
  const [threshold, setThreshold] = useState(80);
  const [csvSymbol, setCsvSymbol] = useState<(typeof V1_SYMBOLS)[number]>("AMD");
  const [csvText, setCsvText] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordNotice, setPasswordNotice] = useState("Enter the same password twice, then save.");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const fileInput = useRef<HTMLInputElement | null>(null);
  async function enableNotifications() {
    const result = await requestNotifications();
    setNotice(result.message);
  }
  async function updatePassword() {
    setPasswordNotice("Checking session...");
    if (!supabase) {
      setPasswordNotice("Supabase is not configured in this build.");
      return;
    }
    if (newPassword.length < 8) {
      setPasswordNotice("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordNotice("Password confirmation does not match.");
      return;
    }
    setPasswordSaving(true);
    setPasswordNotice("Saving password...");
    const { data: userData, error: userError } = await supabase.auth.getUser();
    if (userError || !userData.user?.id) {
      setPasswordSaving(false);
      setPasswordNotice("Your session expired. Use the one-time email link, then save the password again.");
      return;
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setPasswordSaving(false);
    if (error) {
      setPasswordNotice(error.message);
      setNotice(error.message);
      return;
    }
    setNewPassword("");
    setConfirmPassword("");
    setPasswordNotice("Password saved. Next login can stay inside the Home Screen PWA.");
    setNotice("Password updated. Use password login from the Home Screen PWA next time.");
  }
  async function importCsvText(csv: string) {
    try {
      const result = await importManualCsvData({ symbol: csvSymbol, csv });
      setCsvText("");
      setNotice(`${result.symbol}: imported ${result.candlesImported} daily candles to ${result.storage}. Latest bar ${new Date(result.asOf).toLocaleDateString()}.`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "CSV import failed.");
    }
  }
  async function importCsv(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    await importCsvText(await file.text());
  }
  return (
    <section className="space-y-4">
      <button onClick={enableNotifications} className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-violet to-rose px-5 py-4 font-extrabold text-white shadow-glow">
        <BellRing size={18} /> Enable notifications
      </button>
      <section className="glass-card rounded-[2rem] p-5">
        <h2 className="font-extrabold">Password login</h2>
        <p className="mt-2 text-xs leading-5 text-slate-500">Set this once while signed in. After that, login stays inside the Home Screen PWA without opening email links.</p>
        <input
          className="mt-4 w-full rounded-2xl border border-line bg-ink px-3 py-3 text-sm text-white outline-none focus:border-violet"
          placeholder="New password"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
        />
        <input
          className="mt-3 w-full rounded-2xl border border-line bg-ink px-3 py-3 text-sm text-white outline-none focus:border-violet"
          placeholder="Confirm password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
        />
        <button
          type="button"
          onClick={updatePassword}
          disabled={passwordSaving}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-line bg-panel px-4 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-60"
        >
          <KeyRound size={18} /> {passwordSaving ? "Saving..." : "Save password"}
        </button>
        <p className="mt-3 rounded-2xl border border-line bg-ink/60 px-3 py-3 text-xs leading-5 text-slate-300">{passwordNotice}</p>
      </section>
      <section className="glass-card rounded-[2rem] p-5">
        <div className="grid grid-cols-[1fr_auto] gap-3">
          <select className="rounded-2xl border border-line bg-ink px-3 py-3 text-sm" value={csvSymbol} onChange={(event) => setCsvSymbol(event.target.value as (typeof V1_SYMBOLS)[number])}>
            {V1_SYMBOLS.map((symbol) => <option key={symbol}>{symbol}</option>)}
          </select>
          <button onClick={() => fileInput.current?.click()} className="flex items-center justify-center gap-2 rounded-2xl border border-line bg-panel px-4 py-3 text-sm font-extrabold text-white">
            <Upload size={18} /> CSV
          </button>
        </div>
        <input ref={fileInput} className="hidden" type="file" accept=".csv,text/csv" onChange={importCsv} />
        <textarea
          className="mt-3 min-h-32 w-full resize-y rounded-2xl border border-line bg-ink px-3 py-3 font-mono text-xs text-slate-100 outline-none focus:border-violet"
          placeholder="Date,Open,High,Low,Close,Volume"
          value={csvText}
          onChange={(event) => setCsvText(event.target.value)}
        />
        <button
          onClick={() => importCsvText(csvText)}
          disabled={!csvText.trim()}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-line bg-panel px-4 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Upload size={18} /> Import pasted CSV
        </button>
        <p className="mt-3 text-xs leading-5 text-slate-500">Daily CSV fallback expects Date, Open, High, Low, Close, and optional Volume.</p>
      </section>
      <section className="glass-card rounded-[2rem] p-5">
        <label className="text-sm font-extrabold">Minimum signal score: {threshold}</label>
        <input className="mt-4 w-full accent-violet" type="range" min="50" max="95" value={threshold} onChange={(event) => setThreshold(Number(event.target.value))} />
      </section>
      <section className="glass-card rounded-[2rem] p-5">
        <h2 className="font-extrabold">Data limitations</h2>
        <p className="mt-2 text-sm leading-6 text-slate-400">
          V1 covers US stocks and ETFs only. Daily signals use closed daily bars; intraday monitoring checks planned signals and open trades only. Twelve Data free data can be delayed, and every alert is informational.
        </p>
        <p className="mt-3 text-xs text-slate-500">{notice}</p>
      </section>
      <section className="glass-card rounded-[2rem] p-5">
        <h2 className="font-extrabold">Account</h2>
        <p className="mt-2 break-all text-xs leading-5 text-slate-400">Supabase User UID: {userId ?? "Not signed in"}</p>
      </section>
      <section className="glass-card rounded-[2rem] p-5">
        <h2 className="font-extrabold">Risk model</h2>
        <p className="mt-2 text-sm leading-6 text-slate-400">
          Position sizing uses 2% equity risk per trade. Because the watchlist is correlated tech-heavy, the app caps entries at 3 concurrent open positions and warns when total open risk approaches 6%.
        </p>
      </section>
      <section className="glass-card rounded-[2rem] p-5">
        <h2 className="font-extrabold">Required disclaimer</h2>
        <p className="mt-2 text-sm leading-6 text-slate-400">
          This app provides trading signal analysis and notifications only. It does not execute trades and does not guarantee profit.
        </p>
        <p className="mt-2 text-sm leading-6 text-slate-400">
          Realistic expectations: 40-50% win rates can still be viable when winners are larger than losers; long drawdowns are expected.
        </p>
      </section>
    </section>
  );
}

function Level({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-[1.35rem] border border-line bg-panel p-3">
      <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="mt-2 text-lg font-extrabold">{value}</p>
    </div>
  );
}
